// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Reason = any;

type Subscriber<T> = {
    resolve: (value: T) => void;
    reject: (reason: Reason) => void;
};

export class Cache<T> {
    private _updater: (() => Promise<T>) | undefined;
    private _value: T | undefined;
    private _waiting: Subscriber<T>[] = [];
    private _error: Reason | undefined;

    get currentValue(): T | undefined {
        return this._value;
    }

    get(): Promise<T> {
        if (!this._updater) {
            if (this._error) {
                return Promise.reject(this._error);
            }

            if (this._value) {
                return Promise.resolve(this._value);
            }
        }

        return new Promise((resolve, reject) => {
            this._waiting.push({
                resolve: value => resolve(value),
                reject: reason => reject(reason),
            });
        });
    }

    update(updater: () => Promise<T>, options: { conservative?: boolean } = {}): void {
        const { conservative = false } = options;

        if (this._updater) return;

        if (!this.empty() && conservative) return;

        this._updater = updater;
        this._value = undefined;
        this._error = undefined;
        (async () => {
            try {
                const value = await updater();
                this._updater = undefined;
                this.setValue(value);
            } catch (reason) {
                this._updater = undefined;
                this.setError(reason);
            }
        })();
    }

    private empty(): boolean {
        if (this._updater) return false; // TODO: reconsider this behavior
        if (this._value || this._error) return false;
        return true;
    }

    private setValue(value: T): void {
        this._value = value;
        this._waiting.splice(0).forEach(w => w.resolve(value));
    }

    private setError(reason: unknown): void {
        this._error = reason;
        this._waiting.splice(0).forEach(w => {
            try {
                w.reject(reason);
            } catch (e) {
                console.warn(e);
            }
        });
    }
}

export function cache<T>(value: T) {
    const c = new Cache<T>();
    c.update(() => Promise.resolve(value), { conservative: false });
    return c;
}
